import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Chip } from "@mui/material";
import styles from "./DeliverablesList.module.css";
import { isNonEmptyArray } from "../../utils";
import { CustomAccordion, CustomTooltip, InfiniteList2 } from "..";
import ChildReqBox from "./child-req-box/ChildReqBox";
import { ParagraphOutService, SppRequirementService } from "../../../api";
import { useApi } from "../../hooks";
import { isSegFeatureEnabled, SegFlags, translate } from "../../providers";

const cleanFilters = (filters) => {
	let adjFilters = {};
	if (filters?.content?.length > 0) {
		adjFilters = { ...adjFilters, content: filters.content };
	}
	if (isNonEmptyArray(filters?.deliverables?.elements)) {
		adjFilters = { ...adjFilters, deliverables: filters.deliverables };
	}
	if (isNonEmptyArray(filters?.impact)) {
		adjFilters = { ...adjFilters, orphan: true };
	}
	return adjFilters;
};
export default function DeliverablesList({
	filters,
	hasCoverage = false,
	isGlobalDeliverables = false,
	onSelect,
	reqId,
}) {
	const { call: getParagraphsOfDeliverable } = useApi(SppRequirementService.getParagraphsOfDeliverable);
	const { call: getProjectDeliverablesWithChildrenRequirementsCount } = useApi(
		SppRequirementService.getProjectDeliverablesWithChildrenRequirementsCount
	);
	const { call: getParagraphsOfDeliverableByProject } = useApi(
		ParagraphOutService.getParagraphsOfDeliverableByProject
	);
	const { call: getParagraphsOfDeliverableByProjectFiltered } = useApi(
		SppRequirementService.getParagraphsOfDeliverableByProjectFiltered
	);

	const projectId = useSelector(({ context }) => context.project?.id);
	const [deliverables, setDeliverables] = useState([]);
	const [selectedRequirementId, setSelectedRequirementId] = useState(0);
	const [infiniteListRequest, setInfiniteListRequest] = useState(null);
	const hasFilters = useMemo(
		() =>
			filters?.content?.length > 0 ||
			isNonEmptyArray(filters?.deliverables?.elements) ||
			isNonEmptyArray(filters?.impact),
		[filters]
	);
	useEffect(() => {
		if (hasFilters && isGlobalDeliverables) {
			setInfiniteListRequest(
				() =>
					({ page, limit }) =>
						getParagraphsOfDeliverableByProjectFiltered(
							{ projectId },
							{ page, limit },
							{ filters: cleanFilters(filters) }
						)
			);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [getParagraphsOfDeliverableByProjectFiltered, filters, projectId, isGlobalDeliverables]);
	useEffect(() => {
		if (!hasFilters && isSegFeatureEnabled(SegFlags.DELIVERABLES)) {
			setInfiniteListRequest(
				() =>
					({ page, limit }) =>
						getProjectDeliverablesWithChildrenRequirementsCount(
							{ projectId },
							{ page, limit },
							{
								filters: {
									haveChildReq: reqId && hasCoverage ? undefined : true,
									isSelected: reqId ? true : undefined,
									reqId: reqId || undefined,
								},
							}
						)
			);
		}
	}, [
		getProjectDeliverablesWithChildrenRequirementsCount,
		isGlobalDeliverables,
		hasCoverage,
		projectId,
		reqId,
		hasFilters,
	]);
	const handleExpandDeliverable = (deliverable) => {
		if (
			isNonEmptyArray(deliverable.childrenReqs) ||
			isNonEmptyArray(deliverables?.find((deliv) => deliv.id === deliverable.id)?.childrenReqs)
		) {
			return;
		}
		const request = isGlobalDeliverables
			? getParagraphsOfDeliverableByProject({ projectId, deliverableId: deliverable.id })
			: getParagraphsOfDeliverable({ projectId, requirementId: reqId, deliverableId: deliverable.id });
		request
			.then((data) => {
				setDeliverables((prev) =>
					prev.map((deliv) => {
						if (deliv.id === deliverable.id) {
							return { ...deliv, childrenReqs: data };
						}
						return deliv;
					})
				);
			})
			.catch(console.error);
	};
	const handleSelectRequirement = (selection, deliverableName, phaseLocked) => {
		setSelectedRequirementId(selection.id);
		onSelect({ requirement: selection, deliverableName, phaseLocked });
	};
	const handleResponse = (res) => {
		setDeliverables((prev) => {
			let newDeliv = [...prev];
			res.contents.forEach((deliv) => {
				if (!newDeliv.some((nd) => nd.id === deliv.id)) {
					newDeliv = [...newDeliv, deliv];
				}
			});
			return newDeliv;
		});
	};
	const rowRenderer = (row) => (
		<ChildReqBox
			key={row.id}
			childReq={row}
			isGlobalDeliverables={isGlobalDeliverables}
			parentReqId={reqId}
			selectedId={selectedRequirementId}
			onSelect={onSelect}
			onSelectRequirement={handleSelectRequirement}
		/>
	);
	const deliverableRowRenderer = (deliverable) => {
		const { id, reqCount, isCovered, name } = deliverable;
		const currentChildrenReqs = deliverables?.find((deliv) => deliv.id === id)?.childrenReqs || [];
		return (
			<CustomAccordion
				key={id}
				transparent
				customAccordionClass={
					isGlobalDeliverables ? styles["accordationChildren--globalDeliv"] : styles.accordationChildren
				}
				customAccordionDetailsClass={styles.accordionDetails}
				header={
					<CustomTooltip showWhenOverflow title={deliverable?.name}>
						<div
							className={`${styles.delivName} ${
								deliverable.phaseLockedOrArchived && styles.delivNameDisabled
							} ${(!reqCount && styles["delivName--padded"]) || ""}`}
						>
							{deliverable?.name}
							{hasCoverage && (
								<Chip
									className={isCovered ? styles.coverage__covered : styles.coverage__toCover}
									label={
										isCovered
											? translate("common:children-requirements.coverage-status.covered")
											: translate("common:children-requirements.coverage-status.to-cover")
									}
								/>
							)}
						</div>
					</CustomTooltip>
				}
				iconColor="var(--grey-500)"
				onExpand={() => handleExpandDeliverable(deliverable)}
			>
				{(reqCount > 0 && (
					<div className={(isGlobalDeliverables && styles["childReq--padded"]) || ""}>
						{isNonEmptyArray(currentChildrenReqs) &&
							currentChildrenReqs.map((childReq) => (
								<ChildReqBox
									key={childReq.id}
									childReq={childReq}
									deliverableName={name}
									isGlobalDeliverables={isGlobalDeliverables}
									parentReqId={reqId}
									phaseLocked={deliverable.phaseLockedOrArchived}
									selectedId={selectedRequirementId}
									onSelect={onSelect}
									onSelectRequirement={handleSelectRequirement}
								/>
							))}
					</div>
				)) ||
					null}
			</CustomAccordion>
		);
	};
	return (
		<InfiniteList2
			callOnResponse={handleResponse}
			noRowsText={isGlobalDeliverables ? "" : translate("common:children-requirements.deliverables.empty")}
			request={infiniteListRequest}
			rowRenderer={!hasFilters ? deliverableRowRenderer : rowRenderer}
			scrollContainerClassName={styles.infiniteList}
		/>
	);
}
